import {
    DateField,
    EditButton,
    ReferenceField,
    Show,
    TextField,
} from '@/components/admin';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
    AlertOctagon,
    Building2,
    Calendar,
    CheckCircle2,
    Clock,
    FolderOpen,
    Tag,
    UserCheck,
    Wrench,
} from 'lucide-react';
import { useNotify, useRecordContext, useRefresh, useUpdate } from 'ra-core';

const PRIORITY_COLORS: Record<string, string> = {
    low:      'bg-slate-100 text-slate-600 border-slate-200',
    medium:   'bg-blue-50 text-blue-700 border-blue-200',
    high:     'bg-amber-50 text-amber-700 border-amber-200',
    critical: 'bg-red-100 text-red-700 border-red-300',
};

const STATUS_COLORS: Record<string, string> = {
    open:        'bg-blue-50 text-blue-700 border-blue-200',
    in_progress: 'bg-amber-50 text-amber-700 border-amber-200',
    resolved:    'bg-emerald-50 text-emerald-700 border-emerald-200',
    closed:      'bg-slate-100 text-slate-500 border-slate-200',
};

const FullNameField = () => {
    const record = useRecordContext();
    if (!record) return <span>—</span>;
    return <span className="text-sm font-medium">{record.first_name} {record.last_name}</span>;
};

const TicketActions = () => {
    const record = useRecordContext();
    const [update] = useUpdate();
    const notify = useNotify();
    const refresh = useRefresh();

    if (!record) return null;

    const transition = async (newStatus: string, msg: string) => {
        await update('service_tickets', {
            id: record.id,
            data: { status: newStatus, ...(newStatus === 'resolved' ? { resolved_date: new Date().toISOString() } : {}) },
            previousData: record,
        });
        notify(msg, { type: 'success' });
        refresh();
    };

    return (
        <div className="flex items-center gap-2">
            <EditButton />
            {record.status === 'open' && (
                <Button size="sm" variant="outline" className="bg-amber-500 text-white hover:bg-amber-600 hover:text-white"
                    onClick={() => transition('in_progress', 'Ticket moved to In Progress')}>
                    <Clock className="w-4 h-4 mr-2" /> Start Work
                </Button>
            )}
            {record.status === 'in_progress' && (
                <Button size="sm" variant="outline" className="bg-emerald-600 text-white hover:bg-emerald-700 hover:text-white"
                    onClick={() => transition('resolved', 'Ticket marked as Resolved')}>
                    <CheckCircle2 className="w-4 h-4 mr-2" /> Mark Resolved
                </Button>
            )}
            {record.status === 'resolved' && (
                <Button size="sm" variant="outline" className="bg-slate-700 text-white hover:bg-slate-800 hover:text-white"
                    onClick={() => transition('closed', 'Ticket closed')}>
                    <CheckCircle2 className="w-4 h-4 mr-2" /> Close Ticket
                </Button>
            )}
        </div>
    );
};

const ServiceTicketShowContent = () => {
    const record = useRecordContext();
    if (!record) return null;

    const isCritical = record.priority === 'critical';
    const isOverdue = record.due_date && record.status !== 'resolved' && record.status !== 'closed'
        && new Date(record.due_date) < new Date();

    return (
        <div className="max-w-5xl mx-auto space-y-6 mt-4 pb-12 p-6">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 border-b pb-6">
                <div className="flex items-center gap-4">
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center border-2 ${isCritical ? 'bg-red-50 border-red-300' : 'bg-amber-50 border-amber-200'}`}>
                        {isCritical
                            ? <AlertOctagon className="w-7 h-7 text-red-500" />
                            : <Wrench className="w-7 h-7 text-amber-500" />
                        }
                    </div>
                    <div>
                        <div className="flex items-center gap-3 mb-1">
                            <span className="font-mono font-bold text-blue-600 text-sm">{record.ticket_number}</span>
                            <Badge variant="outline" className={`text-[10px] font-bold ${STATUS_COLORS[record.status] ?? ''}`}>
                                {record.status?.replace('_', ' ').toUpperCase()}
                            </Badge>
                            <Badge variant="outline" className={`text-[10px] font-bold ${PRIORITY_COLORS[record.priority] ?? ''}`}>
                                {record.priority?.toUpperCase()} PRIORITY
                            </Badge>
                            {isOverdue && (
                                <Badge variant="outline" className="text-[10px] font-bold bg-red-50 text-red-600 border-red-300">
                                    ⚠️ OVERDUE
                                </Badge>
                            )}
                        </div>
                        <h1 className="text-2xl font-bold text-slate-900">{record.title}</h1>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Details */}
                <div className="md:col-span-2 space-y-4">
                    <Card>
                        <CardHeader className="pb-3 border-b bg-slate-50/50">
                            <CardTitle className="text-xs font-bold uppercase tracking-widest text-slate-500">Description</CardTitle>
                        </CardHeader>
                        <CardContent className="pt-4">
                            <div className="text-sm whitespace-pre-wrap text-slate-700 leading-relaxed">
                                {record.description || <span className="text-slate-400 italic">No description provided.</span>}
                            </div>
                        </CardContent>
                    </Card>

                    {record.resolution_notes && (
                        <Card className="border-emerald-200">
                            <CardHeader className="pb-3 border-b bg-emerald-50/30">
                                <CardTitle className="text-xs font-bold uppercase tracking-widest text-emerald-700 flex items-center gap-2">
                                    <CheckCircle2 className="w-3.5 h-3.5" /> Resolution Notes
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="pt-4">
                                <div className="text-sm whitespace-pre-wrap text-slate-700 leading-relaxed">
                                    <TextField source="resolution_notes" />
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>

                {/* Sidebar */}
                <div className="space-y-4">
                    <Card>
                        <CardHeader className="pb-3 border-b bg-slate-50/50">
                            <CardTitle className="text-xs font-bold uppercase tracking-widest text-slate-500">Details</CardTitle>
                        </CardHeader>
                        <CardContent className="pt-4 space-y-4">
                            <div className="flex items-start gap-3">
                                <Tag className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                                <div>
                                    <span className="text-[10px] font-bold text-slate-400 uppercase block">Category</span>
                                    <span className="text-sm font-medium capitalize">{record.category || '—'}</span>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <UserCheck className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                                <div>
                                    <span className="text-[10px] font-bold text-slate-400 uppercase block">Assigned To</span>
                                    <ReferenceField source="assigned_to" reference="sales" link={false}>
                                        <FullNameField />
                                    </ReferenceField>
                                </div>
                            </div>
                            {record.company_id && (
                                <div className="flex items-start gap-3">
                                    <Building2 className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                                    <div>
                                        <span className="text-[10px] font-bold text-slate-400 uppercase block">Company</span>
                                        <ReferenceField source="company_id" reference="companies" link="show">
                                            <TextField source="name" className="text-sm font-medium" />
                                        </ReferenceField>
                                    </div>
                                </div>
                            )}
                            {record.project_id && (
                                <div className="flex items-start gap-3">
                                    <FolderOpen className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                                    <div>
                                        <span className="text-[10px] font-bold text-slate-400 uppercase block">Project</span>
                                        <ReferenceField source="project_id" reference="projects" link="show">
                                            <TextField source="name" className="text-sm font-medium" />
                                        </ReferenceField>
                                    </div>
                                </div>
                            )}
                            <div className="flex items-start gap-3">
                                <Calendar className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                                <div>
                                    <span className="text-[10px] font-bold text-slate-400 uppercase block">Due Date</span>
                                    <span className={`text-sm font-medium ${isOverdue ? 'text-red-600 font-bold' : ''}`}>
                                        {record.due_date
                                            ? new Date(record.due_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                                            : '—'
                                        }
                                    </span>
                                </div>
                            </div>
                            {record.resolved_date && (
                                <div className="flex items-start gap-3">
                                    <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                                    <div>
                                        <span className="text-[10px] font-bold text-slate-400 uppercase block">Resolved</span>
                                        <DateField source="resolved_date" showTime className="text-sm font-medium text-emerald-600" />
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export const ServiceTicketShow = () => (
    <Show actions={<TicketActions />}>
        <ServiceTicketShowContent />
    </Show>
);
